// components/VideoUpload.tsx
// Video upload with draft auto-save and scheduling

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useFileUpload } from '../firebase/hooks';
import { useDrafts } from '../firebase/useDrafts';
import { useAuth } from '../contexts/AuthContext';
import { PostCategory } from '../types';
import './VideoUpload.css';
import { v4 as uuidv4 } from 'uuid';

export const VideoUpload: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useAuth();
    const { uploadFile, uploadProgress, resetProgress } = useFileUpload();
    const { saveDraft, loadDraft, deleteDraft, autoSaveStatus } = useDrafts('video');

    const [draftId, setDraftId] = useState<string | null>(null);
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [title, setTitle] = useState('');
    const [caption, setCaption] = useState('');
    const [category, setCategory] = useState<PostCategory>('writing-tips');
    const [scheduleDate, setScheduleDate] = useState('');
    const [scheduleTime, setScheduleTime] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Debug auth state
    useEffect(() => {
        console.log('Current user:', currentUser);
        console.log('User authenticated:', !!currentUser);
        console.log('User email:', currentUser?.email);
        console.log('User ID:', currentUser?.uid);
    }, [currentUser]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const resumeDraftId = params.get('draft');

        if (resumeDraftId) {
            loadDraft(resumeDraftId).then(draft => {
                if (draft) {
                    setDraftId(draft.id);
                    setTitle(draft.title || '');
                    setCaption(draft.caption || '');
                    setCategory(draft.category);
                }
            });
        }
    }, [location.search, loadDraft]);


    // Auto-save should only be re-triggered by the text fields; read the
    // file selections and current draftId through a ref so they don't recreate
    // the callback (draftId in particular would otherwise cause a re-save loop).
    const autoSaveLatest = useRef({ videoFile, thumbnailFile, draftId });
    autoSaveLatest.current = { videoFile, thumbnailFile, draftId };

    const handleAutoSave = useCallback(async () => {
        if (!title && !caption) return;
        const { videoFile, thumbnailFile, draftId } = autoSaveLatest.current;
        const id = await saveDraft({
            title,
            caption,
            category,
            videoFileName: videoFile?.name,
            thumbnailFileName: thumbnailFile?.name,
        }, draftId || undefined);
        if (id && !draftId) {
            setDraftId(id);
        }
    }, [title, caption, category, saveDraft]);

    useEffect(() => {
        if (title || caption) {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
            autoSaveTimerRef.current = setTimeout(() => {
                handleAutoSave();
            }, 30000);
        }
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, [title, caption, handleAutoSave]);

    const handleSaveDraft = async () => {
        const id = await saveDraft({
            title,
            caption,
            category,
            videoFileName: videoFile?.name,
            thumbnailFileName: thumbnailFile?.name,
        }, draftId || undefined);
        if (id) {
            alert('Draft saved successfully!');
            navigate('/dashboard');
        }
    };

    const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('video/')) {
                setError('Please select a valid video file');
                return;
            }
            if (file.size > 100 * 1024 * 1024) {
                setError('Video file must be less than 100MB');
                return;
            }
            setVideoFile(file);
            setError('');
        }
    };

    const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Please select a valid image file');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setError('Thumbnail must be less than 5MB');
                return;
            }
            setThumbnailFile(file);
            setError('');
        }
    };

    const handleSubmit = async (e: React.FormEvent, shouldSchedule: boolean = false) => {
        e.preventDefault();

        // Check authentication first
        if (!currentUser) {
            setError('You must be logged in to upload videos');
            return;
        }

        if (!videoFile || !thumbnailFile) {
            setError('Please select both video and thumbnail files');
            return;
        }
        if (!title.trim() || !caption.trim()) {
            setError('Please fill in all required fields');
            return;
        }
        if (title.length < 5 || title.length > 100) {
            setError('Title must be between 5 and 100 characters');
            return;
        }

        if (shouldSchedule && (!scheduleDate || !scheduleTime)) {
            setError('Please select both date and time for scheduling');
            return;
        }

        setIsUploading(true);
        setError('');

        try {
            const videoPath = `barkBoard/videos/${Date.now()}_${videoFile.name}`;
            const videoUrl = await uploadFile(videoFile, videoPath);

            const thumbnailPath = `barkBoard/thumbnails/${Date.now()}_${thumbnailFile.name}`;
            const thumbnailUrl = await uploadFile(thumbnailFile, thumbnailPath);

            let scheduledFor = null;
            let status: 'published' | 'scheduled' = 'published';
            let publishedAt: any = serverTimestamp();

            if (shouldSchedule) {
                const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
                scheduledFor = Timestamp.fromDate(scheduledDateTime);
                status = 'scheduled';
                publishedAt = null;
            }

            const docId = uuidv4(); // Generate once
            const postData = {
                id: docId, // Use same ID
                type: 'video',
                postType: 'admin',
                title: title.trim(),
                caption: caption.trim(),
                category,
                videoUrl,
                thumbnailUrl,
                authorName: 'Omore, the GOAT',
                authorEmail: currentUser.email || '',
                authorId: currentUser.uid,
                createdAt: serverTimestamp(),
                publishedAt,
                scheduledFor,
                status,
                isActive: true,
                viewCount: 0,
                complimentCounts: {
                    creative: 0,
                    funny: 0,
                    inspiring: 0,
                    smart: 0,
                    cool: 0,
                    brave: 0,
                    original: 0,
                    relatable: 0,
                    amazing: 0,
                    wholesome: 0,
                    artistic: 0,
                    magical: 0,
                    legendary: 0,
                    genius: 0,
                    epic: 0,
                    champion: 0,
                    total: 0
                },
            };

            // Use setDoc instead of addDoc
            await setDoc(doc(db, 'barkBoard', 'posts', 'items', docId), postData);

            if (draftId) {
                await deleteDraft(draftId);
            }

            setSuccess(true);
            resetProgress();

            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);

        } catch (err: any) {
            console.error('Upload error:', err);
            setError(err.message || 'Failed to upload video. Please try again.');
            setIsUploading(false);
        }
    };

    const handleCancel = () => {
        navigate('/dashboard');
    };

    if (success) {
        return (
            <div className="upload-container">
                <div className="success-card">
                    <div className="success-icon">✅</div>
                    <h2>Video {scheduleDate ? 'Scheduled' : 'Published'} Successfully!</h2>
                    <p>{scheduleDate ? 'Your video will be published automatically.' : 'Your video is now live on the Bark Board.'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="upload-container">
            <div className="upload-card">
                <div className="upload-header">
                    <h1>Upload Video</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {autoSaveStatus === 'saving' && (
                            <span style={{ fontSize: '12px', color: '#718096' }}>💾 Saving...</span>
                        )}
                        {autoSaveStatus === 'saved' && (
                            <span style={{ fontSize: '12px', color: '#48bb78' }}>✓ Draft saved</span>
                        )}
                        <button onClick={handleCancel} className="close-button">×</button>
                    </div>
                </div>

                <form onSubmit={(e) => handleSubmit(e, false)} className="upload-form">
                    {error && <div className="error-message">{error}</div>}

                    <div className="form-section">
                        <label className="form-label">Video File *</label>
                        <div className="file-upload-area">
                            <input
                                type="file"
                                accept="video/*"
                                onChange={handleVideoChange}
                                disabled={isUploading}
                                id="video-file"
                            />
                            <label htmlFor="video-file" className="file-upload-label">
                                {videoFile ? <span>🎬 {videoFile.name}</span> : <span>Click to select video file (MP4, MOV, AVI - Max 100MB)</span>}
                            </label>
                        </div>
                    </div>

                    <div className="form-section">
                        <label className="form-label">Thumbnail Image *</label>
                        <div className="file-upload-area">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailChange}
                                disabled={isUploading}
                                id="thumbnail-file"
                            />
                            <label htmlFor="thumbnail-file" className="file-upload-label">
                                {thumbnailFile ? <span>🖼️ {thumbnailFile.name}</span> : <span>Click to select thumbnail (JPG, PNG - Max 5MB)</span>}
                            </label>
                        </div>
                    </div>

                    <div className="form-section">
                        <label className="form-label" htmlFor="title">Title *</label>
                        <input
                            type="text"
                            id="title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g., 5 Writing Tips for Beginners"
                            maxLength={100}
                            disabled={isUploading}
                            className="text-input"
                        />
                        <span className="char-count">{title.length}/100</span>
                    </div>

                    <div className="form-section">
                        <label className="form-label" htmlFor="caption">Caption *</label>
                        <textarea
                            id="caption"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder="Brief description of your video..."
                            rows={4}
                            disabled={isUploading}
                            className="textarea-input"
                        />
                    </div>

                    <div className="form-section">
                        <label className="form-label" htmlFor="category">Category *</label>
                        <select
                            id="category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value as PostCategory)}
                            disabled={isUploading}
                            className="select-input"
                        >
                            <option value="writing-tips">Writing Tips</option>
                            <option value="story-inspiration">Story Inspiration</option>
                            <option value="creative-tools">Creative Tools</option>
                            <option value="character-development">Character Development</option>
                            <option value="world-building">World Building</option>
                            <option value="community-spotlight">Community Spotlight</option>
                        </select>
                    </div>

                    {/* Scheduling Section */}
                    <div className="form-section">
                        <label className="form-label">Schedule Publishing (Optional)</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <input
                                type="date"
                                value={scheduleDate}
                                onChange={(e) => setScheduleDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                disabled={isUploading}
                                className="text-input"
                            />
                            <input
                                type="time"
                                value={scheduleTime}
                                onChange={(e) => setScheduleTime(e.target.value)}
                                disabled={isUploading}
                                className="text-input"
                            />
                        </div>
                    </div>

                    {uploadProgress && uploadProgress.status === 'uploading' && (
                        <div className="progress-section">
                            <div className="progress-bar">
                                <div className="progress-fill" style={{ width: `${uploadProgress.progress}%` }} />
                            </div>
                            <p className="progress-text">
                                Uploading {uploadProgress.fileName}... {uploadProgress.progress}%
                            </p>
                        </div>
                    )}

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={handleSaveDraft}
                            className="cancel-button"
                            disabled={isUploading}
                            style={{ background: '#667eea', color: 'white' }}
                        >
                            💾 Save Draft
                        </button>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="cancel-button"
                            disabled={isUploading}
                        >
                            Cancel
                        </button>
                        {scheduleDate && scheduleTime ? (
                            <button
                                type="button"
                                onClick={(e) => handleSubmit(e, true)}
                                className="publish-button"
                                disabled={isUploading || !videoFile || !thumbnailFile || !title || !caption}
                            >
                                {isUploading ? 'Scheduling...' : '📅 Schedule Video'}
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className="publish-button"
                                disabled={isUploading || !videoFile || !thumbnailFile || !title || !caption}
                            >
                                {isUploading ? 'Publishing...' : 'Publish Video'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};